import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";

const Section = styled.section`
  ${tw`w-full`}
  background: #fffbdc;
  padding: clamp(3rem, 2.2rem + 2vw, 4.5rem) 1.5rem clamp(4rem, 2.6rem + 3vw, 6rem);
`;

const Inner = tw.div`max-w-3xl mx-auto`;

const Title = tw.h2`text-3xl sm:text-4xl font-semibold tracking-tight text-[#14133B] text-center uppercase`;
const Subtitle = tw.p`mt-3 text-base sm:text-lg text-[#14133B] leading-relaxed text-center`;

const Form = tw.form`mt-10 flex flex-col gap-6`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  ${tw`text-sm font-semibold uppercase tracking-[0.18em] text-[#14133B]`}
  display: block;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.7);
  border: 2px solid rgba(20, 19, 59, 0.24);
  border-radius: 18px;
  padding: 12px 16px;
  font-size: 1rem;
  color: #14133b;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &::placeholder {
    color: rgba(20, 19, 59, 0.7);
  }

  &:focus {
    outline: none;
    border-color: #14133b;
    transform: translateY(-1px);
  }
`;

const Textarea = styled.textarea`
  background: rgba(255, 255, 255, 0.7);
  border: 2px solid rgba(20, 19, 59, 0.24);
  border-radius: 18px;
  padding: 12px 16px;
  font-size: 1rem;
  color: #14133b;
  min-height: 160px;
  resize: vertical;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &::placeholder {
    color: rgba(20, 19, 59, 0.7);
  }

  &:focus {
    outline: none;
    border-color: #14133b;
    transform: translateY(-1px);
  }
`;

const SubmitButton = styled.button`
  ${tw`inline-flex items-center justify-center uppercase font-semibold self-center`}
  margin-top: 1rem;
  padding: 0.9rem 3rem;
  border-radius: 999px;
  background: #14133b !important;
  color: #fffbdc;
  border: none;
  cursor: pointer;
  letter-spacing: 0.12em;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const HelperText = tw.p`text-sm text-center text-[#14133B] mt-3`;
const ErrorText = tw.span`text-xs text-[#E11D48] font-medium`;

const SuccessBanner = styled.div`
  ${tw`mt-6 rounded-3xl border border-[#14133B] bg-gray-100 px-6 py-5 text-center text-sm sm:text-base text-[#14133B]`}
`;

const ErrorBanner = styled.div`
  ${tw`mt-6 rounded-3xl border border-[#E11D48] bg-gray-100 px-6 py-5 text-center text-sm sm:text-base text-[#E11D48]`}
`;

const ContactForm = React.forwardRef((_, ref) => {
  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    time: "",
    content: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const errors = {};
    if (!values.name.trim()) errors.name = "Please tell us who we are speaking with.";
    if (!values.email.trim()) errors.email = "Please enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = "Please enter a valid email address.";
    if (!values.phone.trim()) errors.phone = "Please enter your phone number.";
    else if (values.phone.replace(/\D/g, "").length < 6) errors.phone = "Please enter a valid phone number.";
    if (!values.time.trim()) errors.time = "Let us know when is a good time to call.";
    if (!values.content.trim()) errors.content = "Share a few details so we can prepare.";
    return errors;
  };

  const scrollToField = (fieldName) => {
    if (typeof document === "undefined") return;
    const node = document.getElementById(`contact-${fieldName}`);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof node.focus === "function") {
        node.focus();
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage(null);
    setSubmitError(null);

    const errors = validate();
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      scrollToField(Object.keys(errors)[0]);
      return;
    }

    setIsSubmitting(true);
    setFieldErrors({});

    try {
      const response = await fetch("/api/SendContact/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          time: values.time.trim(),
          content: values.content.trim(),
          message: values.content.trim(),
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = data?.message || "Unable to send your message.";
        const serverErrors = data?.errors || {};
        setSubmitError(message);
        const entries = Object.entries(serverErrors);
        if (entries.length) {
          const normalizedErrors = entries.reduce((acc, [key, value]) => {
            const normalizedKey = key === "message" ? "content" : key;
            acc[normalizedKey] = value;
            return acc;
          }, {});
          setFieldErrors((prev) => ({ ...prev, ...normalizedErrors }));
          const firstServerError = Object.keys(normalizedErrors)[0];
          if (firstServerError) {
            scrollToField(firstServerError);
          }
        }
        return;
      }

      setSubmitMessage("Thanks for reaching out. A member of the Grain team will follow up shortly!");
      setValues({ name: "", email: "", phone: "", time: "", content: "" });
      setFieldErrors({});
    } catch (error) {
      setSubmitError("Submission failed. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section ref={ref}>
      <Inner>
        <Title>Contact Grain</Title>
        <Subtitle>Fill out the form and we&apos;ll respond within one business day. We can also schedule a call or tasting at your preferred time.</Subtitle>

        <Form onSubmit={handleSubmit}>
          <Field>
            <Label htmlFor="contact-name">Name</Label>
            <Input
              id="contact-name"
              name="name"
              type="text"
              value={values.name}
              onChange={handleChange}
              placeholder="Your full name"
              aria-invalid={Boolean(fieldErrors.name)}
            />
            {fieldErrors.name && <ErrorText>{fieldErrors.name}</ErrorText>}
          </Field>

          <Field>
            <Label htmlFor="contact-email">Email Address*</Label>
            <Input
              id="contact-email"
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              placeholder="name@example.com"
              aria-invalid={Boolean(fieldErrors.email)}
            />
            {fieldErrors.email && <ErrorText>{fieldErrors.email}</ErrorText>}
          </Field>

          <Field>
            <Label htmlFor="contact-phone">Phone Number*</Label>
            <Input
              id="contact-phone"
              name="phone"
              type="tel"
              value={values.phone}
              onChange={handleChange}
              placeholder="Mobile or work phone number"
              aria-invalid={Boolean(fieldErrors.phone)}
            />
            {fieldErrors.phone && <ErrorText>{fieldErrors.phone}</ErrorText>}
          </Field>

          <Field>
            <Label htmlFor="contact-time">Preferred Time</Label>
            <Input
              id="contact-time"
              name="time"
              type="text"
              value={values.time}
              onChange={handleChange}
              placeholder="Let us know when to reach you"
              aria-invalid={Boolean(fieldErrors.time)}
            />
            {fieldErrors.time && <ErrorText>{fieldErrors.time}</ErrorText>}
          </Field>

          <Field>
            <Label htmlFor="contact-content">Content</Label>
            <Textarea
              id="contact-content"
              name="content"
              value={values.content}
              onChange={handleChange}
              placeholder="Share any details, questions, or requests"
              aria-invalid={Boolean(fieldErrors.content)}
            />
            {fieldErrors.content && <ErrorText>{fieldErrors.content}</ErrorText>}
          </Field>

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Sending…" : "Send Message"}
          </SubmitButton>
        </Form>

        <HelperText>Prefer email instead? Reach us at customerservice@grainm.com</HelperText>

        {submitError && <ErrorBanner role="alert">{submitError}</ErrorBanner>}
        {submitMessage && <SuccessBanner role="status">{submitMessage}</SuccessBanner>}
      </Inner>
    </Section>
  );
});

ContactForm.displayName = "ContactForm";

export default ContactForm;
